import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { CheckCircle2, XCircle } from 'lucide-react';
import type { ReviewToken } from '@sleed/bizcommerce-api';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { serverMessage } from '~/lib/api/server-error';
import { useDuplicateReview, useGroupedProducts, type DuplicateResult } from '~/hooks/use-reviews';

interface Props {
	row: ReviewToken | null;
	onOpenChange: (open: boolean) => void;
}

/**
 * Copies an editorial review onto the products of the same group (GroupCode — colour/size siblings), the legacy
 * "assign review to grouped products" popup. Each product is copied on its own and its outcome listed: one that
 * already holds the same review is refused by the server and the others still go through.
 */
export function ReviewCopyDialog({ row, onOpenChange }: Props) {
	const { t } = useTranslation();
	const productId = row?.to_id ?? null;
	const { data: products, isLoading } = useGroupedProducts(row ? productId : null);
	const duplicate = useDuplicateReview();
	const [selected, setSelected] = useState<Set<number>>(new Set());
	const [results, setResults] = useState<DuplicateResult[] | null>(null);

	useEffect(() => {
		setSelected(new Set());
		setResults(null);
	}, [row]);

	const ids = useMemo(() => (products ?? []).map(p => p.id).filter((id): id is number => id != null), [products]);
	const allSelected = ids.length > 0 && ids.every(id => selected.has(id));
	const resultFor = (id: number) => results?.find(r => r.productId === id);

	const toggle = (id: number) =>
		setSelected(prev => {
			const next = new Set(prev);
			if (next.has(id)) next.delete(id);
			else next.add(id);
			return next;
		});

	const copy = async () => {
		if (!row?.id || selected.size === 0) return;
		const outcome = await duplicate.mutateAsync({ id: row.id, productIds: [...selected] });
		setResults(outcome);
		const failed = outcome.filter(r => r.error).length;
		if (failed === 0) toast.success(t('catalog.reviews.copy.done', { count: outcome.length }));
		else toast.warning(t('catalog.reviews.copy.partial', { ok: outcome.length - failed, failed }));
	};

	return (
		<Dialog open={Boolean(row)} onOpenChange={onOpenChange}>
			<DialogContent className="sm:max-w-xl">
				<DialogHeader>
					<DialogTitle>{t('catalog.reviews.copy.title')}</DialogTitle>
					<DialogDescription>{t('catalog.reviews.copy.subtitle', { product: row?.to_title ?? '' })}</DialogDescription>
				</DialogHeader>
				{isLoading ? (
					<Skeleton className="h-40 w-full rounded-md" />
				) : ids.length === 0 ? (
					<p className="py-6 text-center text-sm text-foreground-muted">{t('catalog.reviews.copy.none')}</p>
				) : (
					<div className="flex flex-col gap-2">
						<label className="flex items-center gap-2 text-sm text-foreground-muted">
							<input
								type="checkbox"
								className="h-4 w-4 cursor-pointer"
								checked={allSelected}
								onChange={() => setSelected(allSelected ? new Set() : new Set(ids))}
								aria-label={t('catalog.reviews.copy.selectAll')}
							/>
							{t('catalog.reviews.copy.selectAll')}
						</label>
						<ul className="max-h-80 divide-y divide-border-subtle overflow-y-auto rounded-md border border-border">
							{(products ?? []).map(p => {
								if (p.id == null) return null;
								const result = resultFor(p.id);
								return (
									<li key={p.id} className="flex items-center gap-3 px-3 py-2 text-sm">
										<input
											type="checkbox"
											className="h-4 w-4 cursor-pointer"
											checked={selected.has(p.id)}
											onChange={() => toggle(p.id!)}
											aria-label={p.nm ?? String(p.id)}
										/>
										<span className="shrink-0 font-mono text-[11px] text-foreground-muted">{p.cd}</span>
										<span className="min-w-0 flex-1 truncate">{p.nm}</span>
										{result &&
											(result.error ? (
												<span className="flex items-center gap-1 text-xs text-danger-fg" title={serverMessage(result.error)}>
													<XCircle size={14} /> {serverMessage(result.error)}
												</span>
											) : (
												<CheckCircle2 size={14} className="text-success-fg" aria-label={t('catalog.reviews.copy.copied')} />
											))}
									</li>
								);
							})}
						</ul>
					</div>
				)}
				<DialogFooter>
					<Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
						{t('common.close')}
					</Button>
					<Button type="button" onClick={() => void copy()} disabled={duplicate.isPending || selected.size === 0}>
						{t('catalog.reviews.copy.submit', { count: selected.size })}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}
